import { isValidDomainType } from "./email-domain.js";

const isLocalPreview = ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);

function isValidEmail(value) {
  const address = value.trim();
  const parts = address.split("@");
  if (parts.length !== 2 || address.length > 254) return false;
  const [local, domain] = parts;
  return local.length > 0 && local.length <= 64
    && /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(local)
    && !/(^\.|\.$|\.\.)/.test(local)
    && isValidDomainType(domain);
}

function initContactForm(form) {
  const section = form.closest(".chat-section");
  const title = section.querySelector("[data-chat-title]");
  const titleLead = section.querySelector("[data-chat-lead]");
  const titleAccent = section.querySelector("[data-chat-accent]");
  const previewNote = section.querySelector("[data-preview-note]");
  if (isLocalPreview) previewNote.textContent = "Local preview only. No email was sent.";
  const status = form.querySelector("[data-contact-status]");
  const submitButton = form.querySelector("[type='submit']");
  const submitLabel = form.querySelector("[data-submit-label]");
  const startedAt = form.querySelector("[data-started-at]");
  const emailFeedback = form.querySelector("[data-email-feedback]");
  const emailMarker = form.querySelector("[data-email-marker]");
  const messageFeedback = form.querySelector("[data-message-feedback]");
  const buttonWipe = form.querySelector("[data-button-wipe]");
  const email = form.elements.email;
  const message = form.elements.message;
  const inputs = form.querySelectorAll("input:not([type=hidden]), textarea");
  const touched = new Set();
  let sending = false;
  let completed = false;

  const updateValidation = () => {
    const emailError = !email.value.trim()
      ? "Email required."
      : !isValidEmail(email.value)
        ? "Invalid email."
        : "";
    const emailReady = !emailError;
    const messageError = !message.value.trim()
      ? "Message required."
      : message.value.length > 12000
        ? "Keep your message under 12,000 characters."
        : "";

    emailFeedback.textContent = touched.has("email") ? emailError || "Valid email." : "";
    emailMarker.dataset.state = touched.has("email") ? emailReady ? "valid" : "invalid" : "idle";
    messageFeedback.textContent = touched.has("message") ? messageError : "";
    email.setAttribute("aria-invalid", String(touched.has("email") && Boolean(emailError)));
    message.setAttribute("aria-invalid", String(touched.has("message") && Boolean(messageError)));
    submitButton.disabled = sending || completed || !emailReady || Boolean(messageError);
    return { emailError, messageError, emailReady };
  };

  [email, message].forEach((input) => {
    ["input", "change", "blur"].forEach((eventName) => {
      input.addEventListener(eventName, () => {
        if (input === email && eventName === "blur") email.value = email.value.trim();
        touched.add(input.name);
        updateValidation();
      });
    });
  });

  const revealSent = async () => {
    const animations = [];
    const motion = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const animate = async (element, keyframes, duration) => {
      if (typeof element.animate !== "function") return;
      const animation = element.animate(keyframes, {
        duration, easing: "cubic-bezier(.65,0,.35,1)", fill: "forwards",
      });
      animations.push(animation);
      await animation.finished;
    };
    const showConfirmation = () => {
      form.hidden = true;
      titleLead.textContent = "Look forward to ";
      titleAccent.textContent = "chatting!";
      previewNote.hidden = !isLocalPreview;
      section.dataset.state = "sent";
      form.dataset.state = "sent";
    };

    // Preserve the section's footprint while its form becomes a single heading.
    section.style.setProperty("--sent-height", `${section.getBoundingClientRect().height}px`);
    try {
      if (motion) {
        form.dataset.state = "wiping";
        await animate(buttonWipe, [
          { transform: "scaleX(0)" },
          { transform: "scaleX(1)" },
        ], 600);
        submitLabel.textContent = "Sent!";
        await new Promise((resolve) => window.setTimeout(resolve, 300));
        section.dataset.state = "closing";
        await Promise.all([
          animate(form, [{ opacity: 1 }, { opacity: 0 }], 220),
          animate(title, [
            { opacity: 1, transform: "translateY(0)" },
            { opacity: 0, transform: "translateY(-6px)" },
          ], 220),
        ]);
      }
      showConfirmation();
      if (motion) {
        await animate(title, [
          { opacity: 0, transform: "translateY(10px)" },
          { opacity: 1, transform: "translateY(0)" },
        ], 400);
      }
    } catch {
      // Acceptance is already confirmed; a cancelled effect must not imply failure.
      showConfirmation();
    } finally {
      animations.forEach((animation) => animation.cancel());
    }
    title.focus();
  };

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (sending || completed) return;
    touched.add("email");
    touched.add("message");
    const { emailReady, messageError } = updateValidation();
    if (!emailReady || messageError) {
      (!emailReady ? email : message).focus();
      return;
    }

    const formData = new FormData(form);
    formData.set("email", email.value.trim());
    formData.set("message", message.value.trim());
    sending = true;
    form.dataset.state = "submitting";
    submitButton.disabled = true;
    form.setAttribute("aria-busy", "true");
    inputs.forEach((input) => { input.readOnly = true; });
    submitLabel.textContent = "Sending…";
    status.textContent = "";

    try {
      if (isLocalPreview) {
        // Local previews demonstrate the form without sending email.
        await new Promise((resolve) => window.setTimeout(resolve, 520));
      } else {
        const response = await fetch(form.action, {
          method: "POST",
          body: formData,
          headers: { Accept: "application/json" },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok !== true) {
          throw new Error(result.error || "The message couldn’t be sent. Please try again.");
        }
      }

      completed = true;
      await revealSent();
    } catch (error) {
      form.dataset.state = "idle";
      status.textContent = error.message || "The message couldn’t be sent. Please try again.";
    } finally {
      sending = false;
      form.removeAttribute("aria-busy");
      inputs.forEach((input) => { input.readOnly = completed; });
      submitLabel.textContent = completed ? "Sent!" : "Send message";
      updateValidation();
    }

    // Analytics must not change the outcome of an accepted submission.
    if (completed && !isLocalPreview && typeof window.gtag === "function") {
      try { window.gtag("event", "generate_lead", { method: "contact_form" }); } catch { /* Optional analytics. */ }
    }
  });

  startedAt.value = String(Date.now());
  form.noValidate = true;
  form.dataset.state = "idle";
  section.dataset.state = "idle";
  updateValidation();
  window.addEventListener("pageshow", updateValidation);
}

document.querySelectorAll("[data-contact-form]").forEach(initContactForm);
